import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from '@share/utils';
import { axiosInstanceB2B } from '@share/utils';
import { IMyReward, IMyRewardActivity, IWalletTierBenefits } from '@common-types';
import { setLoginUserWalletData } from '@share/store/slices';

export interface IWalletRewardState {
  myReward: IMyReward;
  myRewardActivities: {
    items: IMyRewardActivity[];
    currentPage: number;
    total: number;
  };
  tierBenefits: IWalletTierBenefits[];
  loading: boolean;
  error: string;
}

const initialState: IWalletRewardState = {
  myReward: {
    current: {
      name: '',
      expires: '',
      point: 0,
      value: 0,
      order: 0
    },
    progress: {
      nextLevel: '',
      expirationDescription: '',
      nextLevelOrder: 0,
      progresses: []
    },
    rewardLevels: []
  },
  tierBenefits: [],
  myRewardActivities: {
    items: [],
    currentPage: 1,
    total: 0
  },
  loading: false,
  error: '',
};

const walletRewardSlice = createSlice({
  name: 'walletReward',
  initialState,
  reducers: {
    setLoading: (state: IWalletRewardState, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setError: (state: IWalletRewardState, { payload }: PayloadAction<string>) => {
      state.error = payload;
    },
    setMyReward: (state: IWalletRewardState, { payload }: PayloadAction<IMyReward>) => {
      state.myReward = payload;
    },
    setMyRewardActivities: (state: IWalletRewardState, { payload }: PayloadAction<any>) => {
      state.myRewardActivities = payload;
    },
    setTierBenefits: (state: IWalletRewardState, { payload }: PayloadAction<IWalletTierBenefits[]>) => {
      state.tierBenefits = payload;
    },
  },
});

export const walletRewardActions = walletRewardSlice.actions;
export const walletRewardReducer = walletRewardSlice.reducer;

export const getMyReward = (userId: string): AppThunk => {
  return async (dispatch) => {
    dispatch(walletRewardActions.setLoading(true));

    try {
      const response = await axiosInstanceB2B.get(`/payments/wallet/reward/${userId}`);
      dispatch(walletRewardActions.setMyReward(response.data.result));
      dispatch(setLoginUserWalletData(response.data.result?.wallet));

      dispatch(walletRewardActions.setLoading(false));

      dispatch(getMyRewardActivities(response.data.result?.wallet?.walletCode));

    } catch (error: any) {
      dispatch(walletRewardActions.setError(error.toString()));
      dispatch(walletRewardActions.setLoading(false));
    }
  };
};

export const getMyRewardActivities = (walletId: string): AppThunk => {
  return async (dispatch) => {
    try {
      const response = await axiosInstanceB2B.get(`/payments/wallet/${walletId}/activities`);
      dispatch(walletRewardActions.setMyRewardActivities({
        items: response.data?.items,
        currentPage: response.data?.currentPage,
        total: response.data?.totalResults
      }));
    } catch (error: any) {
      dispatch(walletRewardActions.setError(error.toString()));
    }
  };
};

export const getTierBenefits = (userId: string): AppThunk => {
  return async (dispatch) => {
    dispatch(walletRewardActions.setLoading(true));

    try {
      const response = await axiosInstanceB2B.get(`/payments/wallet/tiers/benefits/${userId}`);
      dispatch(walletRewardActions.setTierBenefits(response.data.result));
      dispatch(walletRewardActions.setLoading(false));
    } catch (error: any) {
      dispatch(walletRewardActions.setError(error.toString()));
      dispatch(walletRewardActions.setLoading(false));
    }
  };
};
